import { useEffect, useState, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTourStore } from '@/stores/useTourStore';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'expai_tour_completed';

type TourStep = {
  target: string; // CSS selector
  title: string;
  description: string;
  placement?: 'bottom' | 'top' | 'left' | 'right';
};

const STEPS: TourStep[] = [
  {
    target: '[data-sidebar="sidebar"]',
    title: '1. Navegación principal',
    description: 'Desde aquí accedes a todos los módulos: Producción, Calidad, Logística, Mantenimiento y más.',
    placement: 'right',
  },
  {
    target: '[data-tour="global-search"]',
    title: '2. Búsqueda global',
    description: 'Busca órdenes, lotes, series o estaciones. Pulsa Ctrl/⌘+K en cualquier momento para abrir la paleta de comandos.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="ai-agent"]',
    title: '3. Agente EXPAI',
    description: 'Tu copiloto inteligente. Hazle preguntas, ejecuta acciones o pide análisis con IA.',
    placement: 'left',
  },
  {
    target: '[data-tour="main-content"], main',
    title: '4. Dashboard',
    description: 'Tu vista principal con KPIs clave, OEE, alertas y estado en tiempo real de la planta.',
    placement: 'top',
  },
  {
    target: '[data-tour="notifications"]',
    title: '5. Notificaciones',
    description: 'Revisa aquí alertas en tiempo real: nuevas NCs, drifts de modelos, sincronización ERP y más.',
    placement: 'bottom',
  },
];

type Rect = { top: number; left: number; width: number; height: number };

function getElementRect(selector: string): Rect | null {
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}



export function OnboardingTour() {
  const open = useTourStore((s) => s.open);
  const start = useTourStore((s) => s.start);
  const onClose = useTourStore((s) => s.close);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Auto-start on first visit — only once the user is signed in. Before that
  // (login / register pages) the overlay covered the form and looked like the
  // page was broken.
  useEffect(() => {
    if (!user) return;
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      const id = window.setTimeout(() => start(), 800);
      return () => window.clearTimeout(id);
    }
  }, [start, user]);

  const step = STEPS[stepIdx];

  const recomputeRect = useCallback(() => {
    if (!open || !step) return;
    setRect(getElementRect(step.target));
  }, [open, step]);

  useLayoutEffect(() => {
    if (!open) return;
    recomputeRect();
    const id = window.setTimeout(recomputeRect, 50);
    window.addEventListener('resize', recomputeRect);
    window.addEventListener('scroll', recomputeRect, true);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', recomputeRect);
      window.removeEventListener('scroll', recomputeRect, true);
    };
  }, [open, stepIdx, recomputeRect]);

  useEffect(() => {
    if (!open) setStepIdx(0);
  }, [open]);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    onClose();
  };

  if (!open) return null;

  const PAD = 6;
  const highlight = rect
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  // Tooltip position
  let tooltipStyle: React.CSSProperties = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  if (highlight) {
    const placement = step.placement ?? 'bottom';
    const TOOLTIP_W = 340;
    const GAP = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = 0;
    let left = 0;
    switch (placement) {
      case 'bottom':
        top = highlight.top + highlight.height + GAP;
        left = highlight.left + highlight.width / 2 - TOOLTIP_W / 2;
        break;
      case 'top':
        top = highlight.top - GAP - 160;
        left = highlight.left + highlight.width / 2 - TOOLTIP_W / 2;
        break;
      case 'right':
        top = highlight.top + highlight.height / 2 - 80;
        left = highlight.left + highlight.width + GAP;
        break;
      case 'left':
        top = highlight.top + highlight.height / 2 - 80;
        left = highlight.left - GAP - TOOLTIP_W;
        break;
    }
    left = Math.max(12, Math.min(left, vw - TOOLTIP_W - 12));
    top = Math.max(12, Math.min(top, vh - 200));
    tooltipStyle = { top, left, width: TOOLTIP_W, transform: 'none' };
  }

  const isLast = stepIdx === STEPS.length - 1;
  const isFirst = stepIdx === 0;

  return createPortal(
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* Backdrop with cut-out */}
      <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px] pointer-events-auto" onClick={finish}>
        {highlight && (
          <div
            className="absolute rounded-lg ring-2 ring-primary shadow-[0_0_0_9999px_hsl(var(--background)/0.7)] transition-all duration-300 pointer-events-none"
            style={{
              top: highlight.top,
              left: highlight.left,
              width: highlight.width,
              height: highlight.height,
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={cn(
          'absolute pointer-events-auto rounded-lg border bg-card shadow-2xl p-4',
          'animate-in fade-in zoom-in-95 duration-200'
        )}
        style={tooltipStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold leading-tight">{step.title}</h3>
          </div>
          <button
            onClick={finish}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Cerrar tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-4">{step.description}</p>

        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === stepIdx ? 'w-6 bg-primary' : 'w-1.5 bg-muted'
                )}
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              disabled={isFirst}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            {isLast ? (
              <Button size="sm" className="h-7" onClick={finish}>
                Finalizar
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-7"
                onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
              >
                Siguiente <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
